import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useGetList, useGetMany } from "react-admin";
import { fetchListAll } from "@/providers/dataProvider/fetchListAll";
import {
  ResourceName,
  TargetSchema,
  type Claimant,
  type ClaimItem,
  type DocumentLoad,
  type Policy,
  type StoplossClaim,
} from "@/types";
import {
  buildClaimantRows,
  loadIdsFor,
  resolveLoadPolicy,
  type ClaimantRow,
  type RowAttention,
} from "./loadResultModel";

/**
 * The line fetch is one list call filtered by claimant_id_ref (repeated params;
 * the API accepts many) paged through to this ceiling. The server orders claim
 * items by date_of_service DESC and ignores the client's sort, so a single page
 * drops the OLDEST lines first — for a run-out file those are this load's (!4974
 * thread 31122). Paging through takes the whole history up to the ceiling; past
 * it the rows are marked partial rather than silently short. The durable fix is a
 * `document_id_ref` filter on GET /claim-items (follow-up story).
 */
export const LOAD_LINES_MAX = 10_000;

interface UseLoadResultDataResult {
  rows: ClaimantRow[];
  isLoading: boolean;
  /** The line fetch hit the ceiling; Paid in file may be short for some claimants. */
  linesPartial: boolean;
  /** The policy the rows were evaluated against (see resolveLoadPolicy). */
  policy: Policy | undefined;
}

/**
 * Everything the post-load claimants table needs (#7550): the load's
 * claimants, its claim lines (filtered to the ids the load names) and any
 * stop-loss claims it opened. Claimants and stop-loss claims come by id
 * (useGetMany; one request each, small sets). Lines come from one list
 * request over the load's claimants, because getMany is one GET per id and a
 * load can carry thousands of lines.
 */
export const useLoadResultData = (
  load: DocumentLoad | null,
  policies: Policy[] = [],
  attention?: Record<string, RowAttention>,
  documentId?: string
): UseLoadResultDataResult => {
  const dataProvider = useDataProvider();
  const claimantIds = useMemo(() => (load ? loadIdsFor(load, TargetSchema.CLAIMANT).ids : []), [load]);
  const stoplossIds = useMemo(() => (load ? loadIdsFor(load, TargetSchema.STOPLOSS_CLAIM).ids : []), [load]);

  const { data: claimants, isLoading: loadingClaimants } = useGetMany<Claimant>(
    ResourceName.CLAIMANTS,
    { ids: claimantIds },
    { enabled: claimantIds.length > 0 }
  );
  const { data: stoplossById, isLoading: loadingStoploss } = useGetMany<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    { ids: stoplossIds },
    { enabled: stoplossIds.length > 0 }
  );
  // The loader opens / adds to stop-loss claims in post-processing and does not
  // list them in loaded_records; it does stamp the document on the claim's
  // source_documents, so ask for the claims this document fed (#7197 filter).
  const { data: stoplossByDocument, isLoading: loadingStoplossByDoc } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      filter: { document_id_ref: documentId },
      pagination: { page: 1, perPage: 1_000 },
      sort: { field: "created_datetime", order: "DESC" },
    },
    { enabled: !!documentId }
  );
  const stoplossClaims = useMemo(() => {
    const byId = new Map<string, StoplossClaim>();
    for (const c of [...(stoplossById ?? []), ...(stoplossByDocument ?? [])]) byId.set(String(c.id), c);
    return [...byId.values()];
  }, [stoplossById, stoplossByDocument]);
  const { data: lines, isLoading: loadingLines } = useQuery({
    queryKey: [ResourceName.CLAIM_ITEMS, "load-result-lines", claimantIds],
    queryFn: () =>
      fetchListAll<ClaimItem>(
        dataProvider,
        ResourceName.CLAIM_ITEMS,
        { filter: { claimant_id_ref: claimantIds } },
        { maxRecords: LOAD_LINES_MAX }
      ),
    enabled: claimantIds.length > 0,
    staleTime: 30_000,
  });
  const claimItems = lines?.data;
  const linesPartial = !!lines?.truncated;
  const policy = useMemo(
    () => (load ? resolveLoadPolicy(load, claimItems ?? [], policies) : policies[0]),
    [load, claimItems, policies]
  );

  const rows = useMemo(() => {
    if (!load) return [];
    return buildClaimantRows({
      load,
      claimants: claimants ?? [],
      claimItems: claimItems ?? [],
      stoplossClaims,
      policy,
      attention,
      loadCreatedAt: load.created_datetime ? String(load.created_datetime) : undefined,
      partialClaimantIds: linesPartial ? new Set(claimantIds) : new Set<string>(),
    });
  }, [load, claimants, claimItems, stoplossClaims, policy, attention, linesPartial, claimantIds]);

  return {
    rows,
    isLoading:
      (claimantIds.length > 0 && (loadingClaimants || loadingLines)) ||
      (stoplossIds.length > 0 && loadingStoploss) ||
      (!!documentId && loadingStoplossByDoc),
    linesPartial,
    policy,
  };
};
